import { z } from 'zod';
import { accountUserPermissionsSchema, addAccountUserSchema, } from '../../auth/add-account-user-form.js';
import { permissionSchema } from '../../permissions.js';
/**
 * The account's Account Users: list, add, change permissions, remove
 * (users-and-permissions `technical.md`, API contracts). Every route needs the
 * users and permissions permission and isn't allowed while the dashboard is
 * restricted (`patterns/auth-patterns.md`, 2 and 3). The account comes from the
 * session, so it is never in a path or body (`patterns/api-conventions.md`, 3).
 */
/** Page-number pagination (`patterns/api-conventions.md`, 5; ux.md, Long list). */
export const ACCOUNT_USERS_PAGE_SIZE = 50;
export const accountUserSchema = z.object({
    /** The Account User's id, not the login's. */
    id: z.uuid(),
    /** The login's name, trimmed, or `null` when it is blank. */
    name: z.string().nullable(),
    email: z.email(),
    /** Effective permissions in `navPermissionOrder`: all of them for a super user. */
    permissions: z.array(permissionSchema),
    isSuperUser: z.boolean(),
    isEmailVerified: z.boolean(),
    /** The Account User making the request. */
    isYou: z.boolean(),
});
export const listAccountUsersQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(ACCOUNT_USERS_PAGE_SIZE),
});
export const listAccountUsersResponseSchema = z.object({
    /** The caller first, then by name (email address when no name), A to Z. */
    items: z.array(accountUserSchema),
    /** The page returned, which is the last page when a later one was asked for. */
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    /** Every current Account User of the account. */
    total: z.number().int().min(0),
    /**
     * How many of them haven't verified their email address yet, across every
     * page, for the page caption "{n} users · {m} not verified" (`ux.md`, States).
     */
    notVerifiedTotal: z.number().int().min(0),
});
export const listAccountUsers = {
    method: 'GET',
    path: '/api/dashboard/account/users',
    params: z.object({}),
    query: listAccountUsersQuerySchema,
    body: z.object({}),
    response: listAccountUsersResponseSchema,
};
/**
 * `201` with the new Account User. Groundly sends them a verification email with
 * a link to set their own password. An address that already has a login, in
 * any state, is `422 VALIDATION_FAILED` on `email`.
 */
export const addAccountUser = {
    method: 'POST',
    path: '/api/dashboard/account/users',
    params: z.object({}),
    query: z.object({}),
    body: addAccountUserSchema,
    response: accountUserSchema,
};
export const accountUserParamsSchema = z.object({ accountUserId: z.uuid() });
/**
 * Replaces the Account User's permissions with the complete set given. `409
 * RULE_BLOCKED` (`OWN_USERS_AND_PERMISSIONS`, `LAST_USERS_AND_PERMISSIONS_HOLDER`)
 * applies nothing of the save.
 */
export const changeAccountUserPermissions = {
    method: 'PATCH',
    path: '/api/dashboard/account/users/:accountUserId/permissions',
    params: accountUserParamsSchema,
    query: z.object({}),
    body: accountUserPermissionsSchema,
    response: accountUserSchema,
};
/**
 * `204`. `409 RULE_BLOCKED` (`SELF_REMOVAL`, `LAST_USERS_AND_PERMISSIONS_HOLDER`);
 * `404 NOT_FOUND` once they have already been removed.
 */
export const removeAccountUser = {
    method: 'DELETE',
    path: '/api/dashboard/account/users/:accountUserId',
    params: accountUserParamsSchema,
    query: z.object({}),
    body: z.object({}),
    response: z.void(),
};
//# sourceMappingURL=account-users.js.map